import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '~/api/common/dynamicBaseQuery';
import {
    FileRecord,
    FileListQueryArgs,
    PageableResponse,
    UploadFileResponse,
} from '~/features/mdm-ai-assistant-file-management/types/fileManagement.types';
import {
    AI_ASSISTANT_FILES_TAG,
    DEFAULT_PAGE_SIZE,
} from '~/features/mdm-ai-assistant-file-management/constants/fileManagement.constants';

const baseUrl: string = '/v1/admin/ai/kb/documents';

export const mdmAiAssistantFilesApi = createApi({
    reducerPath: 'mdmAiAssistantFilesApi',
    baseQuery: dynamicBaseQuery,
    tagTypes: [AI_ASSISTANT_FILES_TAG],
    endpoints: (builder) => ({
        // List documents (paginated, Spring Pageable).
        getFiles: builder.query<PageableResponse<FileRecord>, FileListQueryArgs | void>({
            query: (args) => {
                const params: Record<string, string | number> = {
                    page: args?.page ?? 0,
                    size: args?.size ?? DEFAULT_PAGE_SIZE,
                };
                // Only include `sort` when explicitly provided (temporarily
                // omitted by default while debugging the empty list).
                if (args?.sort) {
                    params.sort = args.sort;
                }
                return {
                    url: baseUrl,
                    method: 'GET',
                    params,
                };
            },
            providesTags: [AI_ASSISTANT_FILES_TAG],
        }),
        // Upload a PDF document (multipart/form-data). Responds 202 with a
        // PROCESSING document record.
        uploadFile: builder.mutation<UploadFileResponse, File>({
            query: (file) => {
                const formData = new FormData();
                formData.append('file', file, file.name);
                return {
                    url: baseUrl,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: [AI_ASSISTANT_FILES_TAG],
        }),
        // Delete a document by id. Responds 204 No Content.
        deleteFile: builder.mutation<void, string>({
            query: (documentId) => ({
                url: `${baseUrl}/${documentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [AI_ASSISTANT_FILES_TAG],
        }),
    }),
});

export const {
    useGetFilesQuery,
    useUploadFileMutation,
    useDeleteFileMutation,
} = mdmAiAssistantFilesApi;
